import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, TrendingUp, Map, ScanLine, Calculator, GraduationCap, Zap } from "lucide-react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CONFIDENCE_LABEL = (c) => c < 35 ? "Just getting started" : c < 55 ? "Finding your footing" : c < 75 ? "Building confidence" : "In control";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const didLoad = useRef(false);

  const load = async () => {
    const { data } = await client.get("/roadmap");
    if (!data.completed_diagnostic) {
      const pending = localStorage.getItem("fisbo_pending_diagnostic");
      if (pending) {
        try {
          await client.post("/diagnostic/submit", { answers: JSON.parse(pending) });
          localStorage.removeItem("fisbo_pending_diagnostic");
        } catch (e) {
          // keep the stashed answers so the visitor can retry via /diagnostic
        }
        const res = await client.get("/roadmap");
        setData(res.data);
        return;
      }
    }
    setData(data);
  };

  useEffect(() => {
    if (didLoad.current) return;
    didLoad.current = true;
    if (localStorage.getItem("fisbo_new_signup")) {
      navigate("/welcome", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <div className="grid h-64 place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-[var(--tenant-primary)]" /></div>;

  if (!data.completed_diagnostic) {
    return <Navigate to="/diagnostic" replace />;
  }

  const { scores, progress, phases, recommended_phase } = data;
  const recPhase = phases.find((p) => p.key === recommended_phase) || phases[0];
  const nextPhase = phases.find((p) => p.tasks.some((t) => !t.done)) || phases[phases.length - 1];
  const firstWin = data.first_win;

  const tools = [
    { to: "/roadmap", icon: Map, title: "Action Roadmap", desc: "Your week-by-week steps", testid: "tile-roadmap" },
    { to: "/pnl-decoder", icon: ScanLine, title: "P&L Decoder", desc: "Your numbers in plain English", testid: "tile-pnl" },
    { to: "/calculator", icon: Calculator, title: "Profit Calculator", desc: "See small moves compound", testid: "tile-calculator" },
    { to: "/lessons", icon: GraduationCap, title: "Lessons", desc: "Eight training videos", testid: "tile-lessons" },
  ];

  return (
    <div className="fade-up space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Your dashboard</p>
        <h1 className="mt-2 font-head text-3xl font-semibold tracking-tight text-neutral-900">Hi {user?.name?.split(" ")[0]}, here's your one thing this week.</h1>
      </div>

      {/* First Win — immediate insight from the diagnostic */}
      {firstWin && (
        <div className="rounded-2xl border-2 tenant-border bg-white p-6 sm:p-7" data-testid="first-win-card">
          <div className="flex items-start gap-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl tenant-bg"><Zap size={20} className="text-white" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] tenant-text">Your first win</p>
              <p className="mt-1 font-head text-lg font-medium text-neutral-900">Based on your answers, your biggest opportunity is <span className="tenant-text">{firstWin.opportunity}</span>.</p>
              <p className="mt-1 text-neutral-600">{firstWin.tip}</p>
              <Link to={firstWin.link} data-testid="first-win-cta" className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>
                {firstWin.cta} <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Top stats */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-[#E5E2DC] bg-white p-7" data-testid="confidence-card">
          <div className="flex items-center gap-2 text-neutral-500"><TrendingUp size={16} /> <span className="text-sm font-medium">Confidence</span></div>
          <p className="mt-3 font-head text-4xl font-semibold text-neutral-900">{scores.confidence}<span className="text-2xl text-neutral-400">/100</span></p>
          <p className="mt-1 text-sm tenant-text font-medium">{CONFIDENCE_LABEL(scores.confidence)}</p>
          <div className="mt-4 h-2 rounded-full bg-neutral-100"><div className="h-2 rounded-full tenant-bg transition-all duration-700" style={{ width: `${scores.confidence}%` }} /></div>
        </div>
        <div className="rounded-2xl border border-[#E5E2DC] bg-white p-7" data-testid="progress-card">
          <div className="flex items-center gap-2 text-neutral-500"><Map size={16} /> <span className="text-sm font-medium">Roadmap progress</span></div>
          <p className="mt-3 font-head text-4xl font-semibold text-neutral-900">{progress.percent}%</p>
          <p className="mt-1 text-sm text-neutral-500">{progress.done} of {progress.total} steps done</p>
          <div className="mt-4 h-2 rounded-full bg-neutral-100"><div className="h-2 rounded-full tenant-bg transition-all duration-700" style={{ width: `${progress.percent}%` }} /></div>
        </div>
        <div className="rounded-2xl p-7 text-white" style={{ backgroundColor: "var(--tenant-primary)" }} data-testid="recommended-card">
          <p className="text-sm font-medium text-white/80">We recommend starting with</p>
          <p className="mt-2 font-head text-2xl font-semibold">{recPhase.title}</p>
          <p className="mt-1 text-sm text-white/80">{recPhase.subtitle}</p>
          <Link to="/roadmap" data-testid="go-to-roadmap" className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold tenant-text transition-all hover:-translate-y-0.5">Open roadmap <ArrowRight size={16} /></Link>
        </div>
      </div>

      {/* Next step */}
      <div className="rounded-2xl border border-[#E5E2DC] bg-white p-7" data-testid="next-step-card">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Do this next</p>
        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-head text-xl font-medium text-neutral-900">{nextPhase.title} · {nextPhase.chapter}</h3>
            <p className="mt-1 text-neutral-600">{nextPhase.tasks.find((t) => !t.done)?.label || "You've completed this phase — nicely done!"}</p>
          </div>
          <Link to="/roadmap" className="inline-flex shrink-0 items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>Continue <ArrowRight size={16} /></Link>
        </div>
      </div>

      {/* Tools */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {tools.map((t) => (
          <Link key={t.to} to={t.to} data-testid={t.testid} className="group rounded-2xl border border-[#E5E2DC] bg-white p-6 transition-all duration-300 hover:-translate-y-0.5">
            <span className="mb-4 grid h-11 w-11 place-items-center rounded-xl" style={{ backgroundColor: "var(--tenant-primary-light)" }}><t.icon className="tenant-text" strokeWidth={1.5} size={20} /></span>
            <h4 className="font-head text-lg font-medium text-neutral-900">{t.title}</h4>
            <p className="mt-1 text-sm text-neutral-500">{t.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
